import { X } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { iconBtnStyle } from "../styles";

/**
 * Small modal shell (matches ConfirmDialog's chrome). Esc and a click on the
 * backdrop close it. Shared by the Studio's dialogs (approve, test render) and
 * the video page's Details dialog — one chrome, so a modal reads the same
 * wherever it opens.
 */
export function StudioModal({ title, children, onClose, width = 560 }: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  /** Max content width in px (the default fits a confirm dialog; Details
   *  asks for more room for its tabs). Always capped at the viewport. */
  width?: number;
}): React.JSX.Element {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Focus lands on the dialog when it opens and goes back to whatever opened
  // it when it closes (the TourProvider pattern). No trap: Esc and the
  // backdrop are always one gesture away.
  useEffect(() => {
    const opener = document.activeElement;
    dialogRef.current?.focus({ preventScroll: true });
    return () => {
      if (opener instanceof HTMLElement) opener.focus({ preventScroll: true });
    };
  }, []);
  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6,8,14,.66)",
        backdropFilter: "blur(8px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{
          outline: "none",
          background: "var(--ql-bg-raised, #101319)",
          border: "1px solid rgba(255,255,255,.09)",
          borderRadius: 18,
          maxWidth: `min(${width}px, calc(100vw - 24px))`,
          width: "100%",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          padding: 24,
          boxSizing: "border-box",
          boxShadow: "0 24px 60px rgba(0,0,0,.55)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexShrink: 0 }}>
          <div id={titleId} style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em" }}>{title}</div>
          <button type="button" onClick={onClose} style={{ ...iconBtnStyle, width: 30, height: 30, marginLeft: "auto" }} title="Close">
            <X size={14} />
          </button>
        </div>
        <div style={{ minHeight: 0, flex: 1, overflow: "auto" }}>{children}</div>
      </div>
    </div>
  );
}
